const moment = require('moment-timezone');
const { validateCalendar, evaluateDate } = require('./schoolCalendar');

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const DEFAULT_TIME_ZONE = 'Europe/Warsaw';

function invalid(field) {
  const error = new Error(`Invalid lesson occurrence input: ${field}`);
  error.code = 'INVALID_LESSON_OCCURRENCE';
  error.field = field;
  throw error;
}

function localDate(value, field) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
      !moment.utc(value, 'YYYY-MM-DD', true).isValid() || value < '0001-01-01') invalid(field);
  return value;
}

function instant(value, field) {
  if (!(value instanceof Date) && (typeof value !== 'string' || !/T.*(?:Z|[+-]\d{2}:\d{2})$/.test(value))) invalid(field);
  const parsed = value instanceof Date ? moment(value) : moment.parseZone(value, moment.ISO_8601, true);
  if (!parsed.isValid()) invalid(field);
  return parsed.valueOf();
}

function lessonStartInstant(date, startTime, timeZone = DEFAULT_TIME_ZONE) {
  localDate(date, 'date');
  if (typeof startTime !== 'string' || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(startTime)) invalid('startTime');
  if (!moment.tz.zone(timeZone)) invalid('timeZone');
  // Moment uses the earlier offset in an autumn overlap and shifts a spring gap forward.
  return moment.tz(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm', true, timeZone).toDate();
}

function enumerateBaseOccurrences(schoolClass) {
  if (!schoolClass || typeof schoolClass.id !== 'string' || !schoolClass.id.trim()) invalid('schoolClass.id');
  const fromDate = localDate(schoolClass.fromDate, 'schoolClass.fromDate');
  const weekday = WEEKDAYS.indexOf(schoolClass.dayOfWeek);
  if (weekday < 0) invalid('schoolClass.dayOfWeek');
  lessonStartInstant(fromDate, schoolClass.startTime);
  if (schoolClass.tillDate) {
    localDate(schoolClass.tillDate, 'schoolClass.tillDate');
    if (schoolClass.tillDate < fromDate) invalid('schoolClass.tillDate');
  }
  const dates = [];
  if (!schoolClass.isRecurring || !schoolClass.tillDate) dates.push(fromDate);
  else {
    const date = moment.utc(fromDate, 'YYYY-MM-DD', true);
    date.add((weekday - date.day() + 7) % 7, 'days');
    const end = moment.utc(schoolClass.tillDate, 'YYYY-MM-DD', true);
    while (date.valueOf() <= end.valueOf()) {
      dates.push(date.format('YYYY-MM-DD'));
      date.add(7, 'days');
    }
  }
  return dates.map((originalDate, baseIndex) => ({ lessonId: `${schoolClass.id}-${baseIndex}`, baseIndex, originalDate }));
}

function hasFinalAttendance(register) {
  if (!register) return false;
  const status = (register.lessonDetails || {}).attendanceStatus;
  if (status) return status === 'checked';
  return (register.students || []).some(student => typeof student.isPresent === 'boolean' &&
    (!student.participationStatus || student.participationStatus === 'attended' || student.participationStatus === 'absent'));
}

function publishedCalendars(calendars, now) {
  if (!Array.isArray(calendars)) invalid('calendars');
  const revisions = calendars.filter(calendar => {
    if (!calendar || typeof calendar !== 'object') invalid('calendars');
    return calendar.published !== false && (!calendar.status || ['published', 'completed', 'active'].includes(String(calendar.status).toLowerCase()));
  }).map(calendar => {
    const config = validateCalendar(calendar);
    if (typeof calendar.revision !== 'string' || !calendar.revision.trim()) invalid('calendar.revision');
    return { ...config, revision: calendar.revision, effectiveAt: instant(calendar.effectiveAt, 'calendar.effectiveAt') };
  }).filter(calendar => calendar.effectiveAt <= now)
    .sort((left, right) => right.effectiveAt - left.effectiveAt);
  const publicationIds = new Map();
  revisions.forEach(calendar => {
    const key = `${calendar.schoolYearStart}:${calendar.effectiveAt}`;
    if (publicationIds.has(key) && publicationIds.get(key) !== calendar.revision) invalid('calendar.effectiveAt (ambiguous publication instant)');
    publicationIds.set(key, calendar.revision);
  });
  return revisions;
}

/**
 * Resolve the entire stable base series before applying saved overrides and the inclusive
 * range {fromDate, tillDate}. Excluded occurrences remain in the result for impact analysis.
 * calendars is a flat list of published full revisions with opaque string revision IDs
 * and strictly increasing effectiveAt instants within each school year.
 * now must be an explicit instant (Date or ISO timestamp with offset).
 * protectedLessonIds is an optional array supplied by the financial-fact owner: the resolver
 * does not infer approval or billing state. Only past exclusions become historicalProtected;
 * publication impact analysis must block contradictory protected future occurrences.
 * Memberships and saved registers remain caller-owned; calendar policy never cancels people.
 */
function resolveLessonOccurrences({ schoolClass, registers = [], memberships = [], calendars = [], range, now, protectedLessonIds = [] }) {
  const current = instant(now, 'now');
  if (!Array.isArray(registers) || !Array.isArray(memberships) || !Array.isArray(protectedLessonIds)) invalid('collections');
  if (range) {
    localDate(range.fromDate, 'range.fromDate');
    localDate(range.tillDate, 'range.tillDate');
    if (range.tillDate < range.fromDate) invalid('range');
  }
  const revisions = publishedCalendars(calendars, current);
  // Calendar revisions may change teaching policy, never the school's wall-clock zone.
  // A zone migration needs its own explicit handling of historical lesson instants.
  if (new Set(revisions.map(calendar => calendar.timeZone)).size > 1) invalid('calendar.timeZone (timezone changes are unsupported)');
  const registersById = new Map(registers.map(register => [register.lessonId, register]));
  const protectedIds = new Set(protectedLessonIds);
  return enumerateBaseOccurrences(schoolClass).map(base => {
    const lessonRegister = registersById.get(base.lessonId) || null;
    const details = (lessonRegister && lessonRegister.lessonDetails) || {};
    const effectiveDate = localDate(details.date || base.originalDate, 'lessonDetails.date');
    const effectiveStartTime = details.startTime || schoolClass.startTime;
    const year = Number(effectiveDate.slice(0, 4)) - (effectiveDate.slice(5) < '09-01' ? 1 : 0);
    const yearRevisions = revisions.filter(calendar => calendar.schoolYearStart === year);
    const zone = yearRevisions.length ? yearRevisions[0].timeZone : DEFAULT_TIME_ZONE;
    const start = lessonStartInstant(effectiveDate, effectiveStartTime, zone).getTime();
    const past = start <= current;
    const calendar = yearRevisions.find(item => !past || item.effectiveAt <= start);
    const evaluation = calendar ? evaluateDate(effectiveDate, calendar) : { allowed: true, exclusionReasons: [] };
    let calendarDisposition = calendar ? 'allowed' : 'unconfigured';
    if (!evaluation.allowed) {
      calendarDisposition = past && (hasFinalAttendance(lessonRegister) || protectedIds.has(base.lessonId)) ? 'historicalProtected' : 'excluded';
    }
    return { ...base,
      effectiveDate,
      effectiveStartTime,
      lessonRegister,
      calendarDisposition,
      exclusionReasons: evaluation.exclusionReasons,
      calendarRevision: calendar ? calendar.revision : null };
  }).filter(item => !range || (item.effectiveDate >= range.fromDate && item.effectiveDate <= range.tillDate));
}

// A current class can supply a plan for a new future register, never historical evidence.
// Preserve only server-saved metadata on edits and ignore client-supplied nominal fields.
function withNominalLessonSchedule(register, previous, schoolClass, now = Date.now()) {
  const details = { ...(register.lessonDetails || {}) };
  delete details.nominalDayOfWeek;
  delete details.nominalStartTime;
  const saved = previous && previous.lessonDetails;
  if (saved && WEEKDAYS.includes(saved.nominalDayOfWeek) && /^([01]\d|2[0-3]):[0-5]\d$/.test(saved.nominalStartTime || '')) {
    details.nominalDayOfWeek = saved.nominalDayOfWeek;
    details.nominalStartTime = saved.nominalStartTime;
  } else if (!previous && schoolClass) {
    try {
      const occurrence = enumerateBaseOccurrences(schoolClass).find(item => item.lessonId === (register.lessonId || register.id));
      // Be conservative on the same day: a late-created record cannot prove the earlier regular plan.
      if (occurrence && occurrence.originalDate > moment(now).tz(schoolClass.timeZone || DEFAULT_TIME_ZONE).format('YYYY-MM-DD') &&
          WEEKDAYS.includes(schoolClass.dayOfWeek) && /^([01]\d|2[0-3]):[0-5]\d$/.test(schoolClass.startTime || '')) {
        details.nominalDayOfWeek = schoolClass.dayOfWeek;
        details.nominalStartTime = schoolClass.startTime;
      }
    } catch (_) { /* An unresolved occurrence has no trustworthy nominal basis. */ }
  }
  return { ...register, lessonDetails: details };
}

module.exports = { resolveLessonOccurrences, enumerateBaseOccurrences, lessonStartInstant, withNominalLessonSchedule };
